<template>
  <div class="position-relative">
    <LMap
      class="observation-map"
      :zoom="map.zoom"
      :center="map.center"
      @update:center="centerUpdate"
      @update:bounds="boundsUpdate"
      :options="map.options"
      ref="leaflet"
      @ready="addBasemapLayer(BASEMAP_FACTORY)"
    >
      <LMarker
        v-for="observation in observations"
        :key="observation.id"
        :lat-lng="observation.lngLat"
        :draggable="false"
        @click="onMarkerClick(observation.id)"
      >
        <MarkerPin/>
      </LMarker>
    </LMap>
    <div class="position-absolute map-loading" v-if="isBusy">
      <i class="fa fa-spin fa-spinner fa-xl ml-3"></i>
      Loading Observations...
    </div>
  </div>
</template>

<script>
import {mapGetters} from "vuex";
import {LMap, LMarker, LPopup} from "vue2-leaflet";
import MarkerPin from "../../../common/map/MarkerPin.vue";

export default {
  name: "ProjectObservationMap",
  components: {
    LMap,
    LMarker,
    LPopup,
    MarkerPin,
  },
  props: {
    observations: {
      type: Array,
      required: true,
    },
    isBusy: {
      type: Boolean,
      required: true,
    },
    handleClick: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      // used by the map
      map: {
        zoom: 8,
        center: {lng: -90, lat: 40},
        options: {
          zoomControl: false,
        },
        // Used to find the closest assignments before panning finishes, while still allowing panning
        closestCenter: {lng: -90, lat: 40},
        currentBounds: null,
        url: "https://services.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}",
        attribution:
          '&copy; <a target="_blank" rel="noopener noreferrer" href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      },
      // NEW: We need a place to track the active layer just for THIS map component
      currentBasemapLayer: null,
    };
  },
  mounted() {
    setTimeout(() => {
      this.panToAll();
    }, 500);
  },
  watch: {
    observations(newVal) {
      if (newVal) {
        setTimeout(() => {
          this.panToAll();
        }, 500);
      }
    },
    isBusy: {
      immediate: true,
      deep: true,
      handler(newVal) {
        console.log('isBusy', newVal)
      }
    },
    // Watch the basemap factory for changes
    BASEMAP_FACTORY: {
      immediate: true,
      handler(newFactory) {
        console.log('BASEMAP_FACTORY CHANGED: ', newFactory)
        this.addBasemapLayer(newFactory);
      }
    }
  },
  computed: {
    ...mapGetters({
      BASEMAP_FACTORY: "map/BASEMAP_FACTORY",
      isManager: "project/get/isManager",
    }),
    mapObject() {
      return this.$refs.leaflet && this.$refs.leaflet.mapObject
        ? this.$refs.leaflet.mapObject
        : null;
    },
  },
  methods: {
    centerUpdate(center) {
      this.map.center = center;
      this.map.closestCenter = center;
    },
    boundsUpdate(bounds) {
      // Store bounds coordinates to avoid circular reference issues
      if (bounds && bounds._southWest && bounds._northEast) {
        this.map.currentBounds = {
          _southWest: {
            lat: bounds._southWest.lat,
            lng: bounds._southWest.lng,
          },
          _northEast: {
            lat: bounds._northEast.lat,
            lng: bounds._northEast.lng,
          },
          contains: function (latlng) {
            const lat = Array.isArray(latlng) ? latlng[0] : latlng.lat;
            const lng = Array.isArray(latlng) ? latlng[1] : latlng.lng;
            return (
              lat >= this._southWest.lat &&
              lat <= this._northEast.lat &&
              lng >= this._southWest.lng &&
              lng <= this._northEast.lng
            );
          },
        };
      }
    },
    panToAll() {
      if (!(this.$refs.leaflet && this.$refs.leaflet.mapObject)) {
        // if map is not ready, try again shortly
        setTimeout(() => {
          this.panToAll();
        }, 500);
        return;
      }
      this.$nextTick(() => {
        if(!this.observations || this.observations.length === 0) {
          return;
        }
        if (this.observations.length === 1) {
          this.$refs.leaflet.mapObject.flyTo(
            [this.observations[0].lngLat.lat, this.observations[0].lngLat.lng],
            12
          );
          return;
        }
        let bounds = this.observations.map((obs) => [
          obs.lngLat.lat,
          obs.lngLat.lng,
        ]);
        this.$refs.leaflet.mapObject.flyToBounds(bounds, {
          paddingTopLeft: [40, 5],
          paddingBottomRight: [5, 5],
        });
      });
    },
    addBasemapLayer(layerFactory) {
      const map = this.$refs.leaflet && this.$refs.leaflet.mapObject;
      if (!map || typeof layerFactory !== 'function') return;

      // Remove the old layer from this component's map
      if (this.currentBasemapLayer) {
        try {
          map.removeLayer(this.currentBasemapLayer);
        } catch (e) {}
      }

      // Create a new layer instance
      const freshLayer = layerFactory();
      if (!freshLayer) return;

      // Layer is added to map and saved for future
      freshLayer.addTo(map);
      this.currentBasemapLayer = freshLayer;

      // Force reload the map
      map.invalidateSize();
    },
    onMarkerClick(observationId) {
      this.handleClick(observationId)
    },
  },
};
</script>

<style scoped lang="scss">
.map-loading {
  top: 10px;
  left: 50%;
  transform: translate(-50%, 0);
  z-index: 999;
  background-color: rgba(255, 255, 255, 0.8);
  padding: 2px;
  border-radius: 10px;
}

.observation-map {
  width: 100%;
  height: 50vh;
  min-height: 400px;
}
</style>
